<template>
    <div
        v-if="shouldShowSelector"
        class="flex items-center gap-2 pb-1 sm:pb-4 self-stretch"
    >
        <!-- Card thumbnails (max 3 + placeholder) -->
        <div class="flex items-center -space-x-4">
            <template
                v-for="(card, index) in displayedCards"
                :key="card.connected_account_id"
            >
                <ImageLoader
                    class="block rounded-sm border border-white"
                    :style="{ zIndex: displayedCards.length - index }"
                >
                    <template #image>
                        <img
                            :src="getCardImageUrl(card)"
                            class="h-6 w-[38px] rounded-sm"
                            loading="eager"
                        />
                    </template>
                    <template #preloader>
                        <div class="h-6 w-[38px] rounded-sm bg-slate-100"></div>
                    </template>
                    <template #error>
                        <div
                            class="h-6 w-[38px] rounded-sm bg-slate-200 flex items-center justify-center"
                        >
                            <Icon
                                icon="credit-card"
                                class="w-5 h-4 text-slate-400"
                            />
                        </div>
                    </template>
                </ImageLoader>
            </template>
            <!-- Placeholder for additional cards -->
            <div
                v-if="remainingCount > 0"
                class="h-6 w-[38px] rounded-sm bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-500 border border-white"
            >
                +{{ remainingCount }}
            </div>
        </div>

        <!-- Counter -->
        <span class="text-slate-600 dark:text-slate-400 text-sm">
            {{ displayedCount }} of {{ counterTotal }}
            {{ counterTotal === 1 ? 'card' : 'cards' }}
        </span>

        <!-- Select button -->
        <button
            type="button"
            class="text-primary text-sm font-medium cursor-pointer bg-transparent border-none p-0 hover:text-primary-dark"
            @click="$emit('open-modal')"
        >
            Select
        </button>

        <!-- Separator and Upgrade link (only when banner is dismissed) -->
        <template v-if="bannerDismissed">
            <span class="text-slate-300 dark:text-slate-700">|</span>
            <button
                type="button"
                class="flex items-center gap-1 sm:gap-2 text-primary text-sm font-medium cursor-pointer bg-transparent border-none p-0"
                @click="$emit('open-upgrade')"
            >
                <Icon icon="star" class="w-4 h-4" />
                <span class="sm:hidden">Upgrade</span>
                <span class="hidden sm:inline">Upgrade for unlimited</span>
            </button>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

defineEmits(['open-modal', 'open-upgrade']);

const store = useCardBenefitsStore();
const {
    isSubscribed,
    availableCardsForTracking,
    bannerDismissed,
    selectedCards,
    hasActiveFilters,
    maxTrackedCards,
    walletUsersCount,
} = storeToRefs(store);

const cdnUrl = computed(() => window.cdnUrl);

const getCardImageUrl = (card) => {
    if (card.image?.path && card.image?.file_name) {
        return `${cdnUrl.value}/${card.image.path}/_small/${card.image.file_name}`;
    }
    return null;
};

const totalCards = computed(() => {
    return availableCardsForTracking.value.length;
});

// Show selector for all free users (not just multi-user)
const shouldShowSelector = computed(() => {
    return !isSubscribed.value && totalCards.value > 0;
});

const trackedCards = computed(() => {
    const tracked = availableCardsForTracking.value.filter(
        (card) => card.is_tracked
    );
    // If no cards are explicitly tracked, use the first available card as default
    if (tracked.length === 0 && availableCardsForTracking.value.length > 0) {
        return [availableCardsForTracking.value[0]];
    }
    // Sort to prioritize owner's cards first (cards from primary user)
    // Owner's cards have lower user_id typically, or we can check if user is primary
    return tracked.sort((a, b) => {
        // Primary user's cards come first
        if (a.is_primary_user && !b.is_primary_user) return -1;
        if (!a.is_primary_user && b.is_primary_user) return 1;
        return 0;
    });
});

const trackedCount = computed(() => {
    return trackedCards.value.length;
});

// Display count: uses selected card filter count when filters are active,
// otherwise falls back to tracked cards count
const displayedCount = computed(() => {
    if (hasActiveFilters.value && selectedCards.value.length > 0) {
        return selectedCards.value.length;
    }
    return trackedCount.value;
});

const maxDisplayed = 3;

const displayedCards = computed(() => {
    return trackedCards.value.slice(0, maxDisplayed);
});

const remainingCount = computed(() => {
    return Math.max(0, trackedCards.value.length - maxDisplayed);
});

// Counter total: for multi-user free accounts, show maxTrackedCards (= number of users)
// For single-user free accounts, show totalCards
const counterTotal = computed(() => {
    if (walletUsersCount.value > 1) {
        return maxTrackedCards.value;
    }
    return totalCards.value;
});
</script>
